import { TextFieldProps } from "@mui/material";
import { DatePickerProps, DatePicker as MuiDatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import React from "react";

interface Props extends Omit<DatePickerProps<DateTime>, "value" | "onChange"> {
  /** Props to pass through to the rendered input. */
  inputProps?: TextFieldProps;
  /** The controlled iso date value of the picker. Must pass a `string` if controlled, else `undefined` if uncontrolled. */
  value?: string;
  /** Callback when the string value of the render input changes. */
  onChange?: (stringDate: string) => void;
  /** Callback when the datepicker has a new valid `DateTime` value or is cleared. */
  onAccepted?: (date?: DateTime) => void;
}

interface ITextFieldOnBlurSlot {
  onBlur?: (ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const DatePicker: React.FC<Props> = ({ value, inputProps, onChange, onAccepted, slotProps, ...rest }) => {
  const pickerValue = value ? DateTime.fromISO(value) : null;
  const pendingAcceptedRef = React.useRef<{ value?: DateTime; dirty: boolean }>({ dirty: false });

  const commitPendingAccepted = React.useCallback(() => {
    if (!pendingAcceptedRef.current.dirty) return;
    onAccepted?.(pendingAcceptedRef.current.value);
    pendingAcceptedRef.current.dirty = false;
  }, [onAccepted]);

  return (
    <MuiDatePicker
      {...rest}
      format="MM/dd/yyyy"
      value={pickerValue?.isValid ? pickerValue : null}
      onChange={newValue => {
        if (!newValue) {
          onChange?.("");
          pendingAcceptedRef.current = { value: undefined, dirty: true };
          return;
        }

        if (!newValue.isValid) {
          return;
        }

        onChange?.(newValue.toISODate() ?? "");
        pendingAcceptedRef.current = { value: newValue, dirty: true };
      }}
      onAccept={newValue => {
        onAccepted?.(newValue && newValue.isValid ? newValue : undefined);
        pendingAcceptedRef.current.dirty = false;
      }}
      slotProps={{
        ...slotProps,
        textField: {
          ...slotProps?.textField,
          ...inputProps,
          onBlur: event => {
            const existingSlotOnBlur = (slotProps?.textField as unknown as ITextFieldOnBlurSlot | undefined)?.onBlur;
            existingSlotOnBlur?.(event);
            inputProps?.onBlur?.(event);
            commitPendingAccepted();
          }
        }
      }}
    />
  );
};

export default DatePicker;
